import React, { useCallback, useEffect, useState } from "react";
import { Alert, Badge, Button, Card, Col, Form, Row, Spinner, Table } from "react-bootstrap";
import { FaBookOpen, FaCheckDouble, FaCog, FaPlus, FaTools, FaTrashAlt } from "react-icons/fa";
import {
  autocompletarConfiguracionContableEmpresa,
  diagnosticarConfiguracionContable,
  guardarCuentaContable,
  guardarReglaAsiento,
  importarConfiguracionPlanillas,
  inactivarCuentaContable,
  inicializarPlanCuentasBase,
  obtenerPlanCuentasPorEmpresa,
  obtenerReglasAsientoPorEmpresa,
} from "../../../services/contabilidad/planCuentasService";
import { logBusinessAction } from "../../../services/AuditService";

const BTN = { background: "linear-gradient(135deg, #3a3a3a 0%, #606060 100%)", border: "none", color: "white" };
const INITIAL_CUENTA = { codigo: "", nombre: "", clase: "", naturaleza: "GASTO", nivel: "6" };
const INITIAL_REGLA = { tipo_gasto: "GASTO_ADMINISTRATIVO", cuenta_debe: "", cuenta_haber: "421201", cuenta_destino: "" };

const PlanCuentasPCGE = ({ usuario }) => {
  const rucEmpresa = usuario?.ruc || "";
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState("");
  const [success, setSuccess] = useState("");
  const [cuentas, setCuentas] = useState([]);
  const [reglas, setReglas] = useState([]);
  const [diagnostico, setDiagnostico] = useState(null);
  const [cuentaForm, setCuentaForm] = useState(INITIAL_CUENTA);
  const [reglaForm, setReglaForm] = useState(INITIAL_REGLA);

  const cargar = useCallback(async () => {
    if (!rucEmpresa) return;
    try {
      setLoading(true);
      await inicializarPlanCuentasBase(rucEmpresa);
      const [cuentasData, reglasData] = await Promise.all([obtenerPlanCuentasPorEmpresa(rucEmpresa), obtenerReglasAsientoPorEmpresa(rucEmpresa)]);
      setCuentas(cuentasData); setReglas(reglasData);
      setDiagnostico(await diagnosticarConfiguracionContable(rucEmpresa));
    } catch (e) { setError("No se pudo cargar configuración contable."); }
    finally { setLoading(false); }
  }, [rucEmpresa]);

  useEffect(() => { cargar(); }, [cargar]);

  const guardarCuenta = async () => {
    if (!cuentaForm.codigo || !cuentaForm.nombre) { setError("Código y nombre son obligatorios."); return; }
    try {
      setSaving(true);
      setError("");
      await guardarCuentaContable({ rucEmpresa, cuenta: cuentaForm });
      setSuccess("Cuenta contable guardada.");

      // AUDITORÍA CUENTA PCGE
      try {
        await logBusinessAction("REGISTRO_CUENTA_PCGE", "GestionContable", {
          codigo: cuentaForm.codigo,
          nombre: cuentaForm.nombre,
          clase: cuentaForm.clase,
          naturaleza: cuentaForm.naturaleza
        }, usuario);
      } catch (e) {
        console.warn("No se pudo registrar la auditoría de la cuenta PCGE", e);
      }

      setCuentaForm(INITIAL_CUENTA);
      await cargar();
    }
    catch (e) { setError(e.message || "No se pudo guardar la cuenta."); }
    finally { setSaving(false); }
  };

  const guardarRegla = async () => {
    if (!reglaForm.cuenta_debe || !reglaForm.cuenta_haber) { setError("Cuentas debe/haber son obligatorias."); return; }
    try {
      setSaving(true);
      setError("");
      await guardarReglaAsiento({ rucEmpresa, regla: reglaForm });
      setSuccess("Regla de amarre guardada.");

      // AUDITORÍA REGLA PCGE
      try {
        await logBusinessAction("REGISTRO_REGLA_PCGE", "GestionContable", {
          tipoGasto: reglaForm.tipo_gasto,
          debe: reglaForm.cuenta_debe,
          haber: reglaForm.cuenta_haber,
          destino: reglaForm.cuenta_destino
        }, usuario);
      } catch (e) {
        console.warn("No se pudo registrar la auditoría de regla PCGE", e);
      }

      setReglaForm(INITIAL_REGLA);
      await cargar();
    }
    catch (e) { setError(e.message || "No se pudo guardar la regla."); }
    finally { setSaving(false); }
  };

  const inactivarCuenta = async (codigo) => {
    if (!window.confirm(`¿Inactivar la cuenta ${codigo}?`)) return;
    try {
      setError("");
      await inactivarCuentaContable({ rucEmpresa, codigo });
      setSuccess(`Cuenta ${codigo} inactivada.`);

      // AUDITORÍA INACTIVACIÓN PCGE
      try {
        await logBusinessAction("INACTIVACION_CUENTA_PCGE", "GestionContable", {
          codigo
        }, usuario);
      } catch (e) {
        console.warn("No se pudo registrar la auditoría de inactivación PCGE", e);
      }

      await cargar();
    }
    catch (e) { setError("No se pudo inactivar la cuenta."); }
  };

  const importarDesdePlanillas = async () => {
    try { setSaving(true); setError(""); const r = await importarConfiguracionPlanillas(rucEmpresa); setSuccess(`Importación: ${r.cuentasImportadas} cuentas y ${r.reglasImportadas} reglas.`); await cargar(); }
    catch (e) { setError("No se pudo importar la configuración de planillas."); }
    finally { setSaving(false); }
  };

  const autocompletar = async () => {
    try { setSaving(true); setError(""); const r = await autocompletarConfiguracionContableEmpresa(rucEmpresa); setSuccess(`Autocompletado: ${r.cuentasCreadas} creadas y ${r.reglasCreadas} reglas.`); await cargar(); }
    catch (e) { setError("No se pudo autocompletar la configuración."); }
    finally { setSaving(false); }
  };

  if (!rucEmpresa) return <Alert variant="warning" className="border-0 shadow-sm">No se encontró RUC de empresa para configurar el PCGE.</Alert>;

  return (
    <div>
      {error && <Alert variant="danger" onClose={() => setError("")} dismissible className="border-0 shadow-sm">{error}</Alert>}
      {success && <Alert variant="success" onClose={() => setSuccess("")} dismissible className="border-0 shadow-sm">{success}</Alert>}

      {/* Banner de estado */}
      <Card className="shadow-sm border-0 mb-4" style={{ borderRadius: "15px" }}>
        <Card.Body className="p-4">
          <Row className="align-items-center">
            <Col md={1} className="text-center">
              <div className={`rounded-3 p-3 d-inline-block ${diagnostico?.ok ? "bg-success bg-opacity-10" : "bg-warning bg-opacity-10"}`}>
                {diagnostico?.ok ? <FaCheckDouble className="text-success fs-5" /> : <FaTools className="text-warning fs-5" />}
              </div>
            </Col>
            <Col md={6}>
              <h5 className="mb-1 fw-semibold">Estado de Configuración</h5>
              <p className="text-muted mb-0 small">
                {diagnostico?.ok ? "Configuración contable completa para operación básica." : `Faltantes: ${(diagnostico?.cuentasFaltantes || []).join(", ") || "—"}`}
              </p>
            </Col>
            <Col md={5} className="d-flex gap-2 justify-content-end mt-3 mt-md-0">
              <Button className="rounded-pill px-3 border-0 shadow-sm" style={BTN} onClick={autocompletar} disabled={saving || loading}>
                {saving ? "Procesando..." : "Autocompletado Base"}
              </Button>
              <Button variant="light" className="rounded-pill px-3 fw-semibold text-muted" onClick={importarDesdePlanillas} disabled={saving || loading}>
                {saving ? "Importando..." : "Migrar Planillas"}
              </Button>
            </Col>
          </Row>
        </Card.Body>
      </Card>

      {/* Formularios */}
      <Row className="g-3 mb-4">
        <Col lg={6}>
          <Card className="border-0 shadow-sm h-100" style={{ borderRadius: "15px", overflow: "hidden" }}>
            <Card.Header className="text-white py-3" style={{ background: "linear-gradient(135deg, #3a3a3a 0%, #606060 100%)", borderBottom: "3px solid #9ca3af" }}>
              <h6 className="mb-0 fw-bold d-flex align-items-center gap-2"><FaPlus />Nueva Cuenta PCGE</h6>
            </Card.Header>
            <Card.Body className="p-4" style={{ backgroundColor: "#f8f9fa" }}>
              <Row className="g-3">
                <Col md={4}>
                  <Form.Label className="small fw-bold text-uppercase text-muted">Código</Form.Label>
                  <Form.Control className="border-0 shadow-sm py-2" style={{ borderRadius: "10px" }} value={cuentaForm.codigo} onChange={e => setCuentaForm(p => ({ ...p, codigo: e.target.value }))} placeholder="60.1.1.01" />
                </Col>
                <Col md={8}>
                  <Form.Label className="small fw-bold text-uppercase text-muted">Nombre</Form.Label>
                  <Form.Control className="border-0 shadow-sm py-2" style={{ borderRadius: "10px" }} value={cuentaForm.nombre} onChange={e => setCuentaForm(p => ({ ...p, nombre: e.target.value }))} />
                </Col>
                <Col md={4}>
                  <Form.Label className="small fw-bold text-uppercase text-muted">Clase</Form.Label>
                  <Form.Control className="border-0 shadow-sm py-2" style={{ borderRadius: "10px" }} value={cuentaForm.clase} onChange={e => setCuentaForm(p => ({ ...p, clase: e.target.value }))} />
                </Col>
                <Col md={4}>
                  <Form.Label className="small fw-bold text-uppercase text-muted">Naturaleza</Form.Label>
                  <Form.Select className="border-0 shadow-sm py-2" style={{ borderRadius: "10px" }} value={cuentaForm.naturaleza} onChange={e => setCuentaForm(p => ({ ...p, naturaleza: e.target.value }))}>
                    <option>ACTIVO</option><option>PASIVO</option><option>GASTO</option><option>INGRESO</option><option>DESTINO</option>
                  </Form.Select>
                </Col>
                <Col md={4}>
                  <Form.Label className="small fw-bold text-uppercase text-muted">Nivel</Form.Label>
                  <Form.Control className="border-0 shadow-sm py-2" style={{ borderRadius: "10px" }} value={cuentaForm.nivel} onChange={e => setCuentaForm(p => ({ ...p, nivel: e.target.value }))} />
                </Col>
              </Row>
            </Card.Body>
            <Card.Footer className="border-0 bg-light p-3" style={{ borderRadius: "0 0 15px 15px" }}>
              <div className="d-flex justify-content-end">
                <Button className="rounded-pill px-4 border-0 shadow-sm fw-bold" style={BTN} onClick={guardarCuenta} disabled={saving || loading}>
                  {saving ? "..." : "Registrar Cuenta"}
                </Button>
              </div>
            </Card.Footer>
          </Card>
        </Col>
        <Col lg={6}>
          <Card className="border-0 shadow-sm h-100" style={{ borderRadius: "15px", overflow: "hidden" }}>
            <Card.Header className="text-white py-3" style={{ background: "linear-gradient(135deg, #3a3a3a 0%, #606060 100%)", borderBottom: "3px solid #9ca3af" }}>
              <h6 className="mb-0 fw-bold d-flex align-items-center gap-2"><FaCog />Regla de Asiento Automático</h6>
            </Card.Header>
            <Card.Body className="p-4" style={{ backgroundColor: "#f8f9fa" }}>
              <Row className="g-3">
                <Col md={6}>
                  <Form.Label className="small fw-bold text-uppercase text-muted">Tipo de Gasto</Form.Label>
                  <Form.Select className="border-0 shadow-sm py-2" style={{ borderRadius: "10px" }} value={reglaForm.tipo_gasto} onChange={e => setReglaForm(p => ({ ...p, tipo_gasto: e.target.value }))}>
                    <option>COSTO_DIRECTO</option><option>GASTO_ADMINISTRATIVO</option><option>GASTO_VENTAS</option><option>ACTIVO_FIJO</option>
                  </Form.Select>
                </Col>
                <Col md={6}>
                  <Form.Label className="small fw-bold text-uppercase text-muted">Cuenta Debe (6x)</Form.Label>
                  <Form.Control className="border-0 shadow-sm py-2 fw-bold" style={{ borderRadius: "10px" }} value={reglaForm.cuenta_debe} onChange={e => setReglaForm(p => ({ ...p, cuenta_debe: e.target.value }))} />
                </Col>
                <Col md={6}>
                  <Form.Label className="small fw-bold text-uppercase text-muted">Cuenta Haber</Form.Label>
                  <Form.Control className="border-0 shadow-sm py-2 fw-bold" style={{ borderRadius: "10px" }} value={reglaForm.cuenta_haber} onChange={e => setReglaForm(p => ({ ...p, cuenta_haber: e.target.value }))} />
                </Col>
                <Col md={6}>
                  <Form.Label className="small fw-bold text-uppercase text-muted">Cuenta Destino (9x)</Form.Label>
                  <Form.Control className="border-0 shadow-sm py-2 fw-bold" style={{ borderRadius: "10px" }} value={reglaForm.cuenta_destino} onChange={e => setReglaForm(p => ({ ...p, cuenta_destino: e.target.value }))} />
                </Col>
              </Row>
            </Card.Body>
            <Card.Footer className="border-0 bg-light p-3" style={{ borderRadius: "0 0 15px 15px" }}>
              <div className="d-flex justify-content-end">
                <Button className="rounded-pill px-4 border-0 shadow-sm fw-bold" style={BTN} onClick={guardarRegla} disabled={saving || loading}>
                  {saving ? "..." : "Guardar Regla"}
                </Button>
              </div>
            </Card.Footer>
          </Card>
        </Col>
      </Row>

      {/* Tablas */}
      <Row className="g-3">
        <Col lg={7}>
          <Card className="border-0 shadow-sm" style={{ borderRadius: "15px", overflow: "hidden" }}>
            <div style={{ backgroundColor: "#f8f9fa", borderBottom: "2px solid #dee2e6", padding: "12px 16px" }}>
              <span className="fw-semibold text-secondary small text-uppercase d-flex align-items-center gap-2"><FaBookOpen />Plan de Cuentas Activo ({cuentas.length})</span>
            </div>
            <div style={{ maxHeight: 380, overflowY: "auto" }}>
              {loading ? <div className="text-center py-5"><Spinner animation="border" size="sm" /></div> : (
                <Table hover className="align-middle bg-white mb-0" style={{ fontSize: "0.9rem" }}>
                  <thead style={{ backgroundColor: "#f8f9fa", borderBottom: "2px solid #dee2e6" }}>
                    <tr>
                      <th className="py-2 px-4 text-secondary fw-semibold">Código</th>
                      <th className="py-2 px-4 text-secondary fw-semibold">Nombre</th>
                      <th className="py-2 px-4 text-secondary fw-semibold text-center">Tipo</th>
                      <th className="py-2 px-4 text-secondary fw-semibold text-center">Acción</th>
                    </tr>
                  </thead>
                  <tbody>
                    {cuentas.length === 0 ? <tr><td colSpan={4} className="text-center text-muted py-5">No hay cuentas configuradas.</td></tr> : cuentas.map(c => (
                      <tr key={c.id} style={{ borderBottom: "1px solid #f1f3f4" }}>
                        <td className="px-4 py-2 fw-bold text-dark">{c.codigo}</td>
                        <td className="px-4 py-2">{c.nombre}</td>
                        <td className="px-4 py-2 text-center">
                          <Badge bg={c.naturaleza === "GASTO" ? "danger" : c.naturaleza === "ACTIVO" ? "success" : "secondary"} className="rounded-pill px-2">{c.naturaleza}</Badge>
                        </td>
                        <td className="px-4 py-2 text-center">
                          <Button size="sm" variant="link" className="text-danger p-0" onClick={() => inactivarCuenta(c.codigo)}><FaTrashAlt /></Button>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </Table>
              )}
            </div>
          </Card>
        </Col>
        <Col lg={5}>
          <Card className="border-0 shadow-sm" style={{ borderRadius: "15px", overflow: "hidden" }}>
            <div style={{ backgroundColor: "#f8f9fa", borderBottom: "2px solid #dee2e6", padding: "12px 16px" }}>
              <span className="fw-semibold text-secondary small text-uppercase d-flex align-items-center gap-2"><FaTools />Reglas de Amarre ({reglas.length})</span>
            </div>
            <div style={{ maxHeight: 380, overflowY: "auto" }}>
              <Table hover className="align-middle bg-white mb-0" style={{ fontSize: "0.9rem" }}>
                <thead style={{ backgroundColor: "#f8f9fa", borderBottom: "2px solid #dee2e6" }}>
                  <tr>
                    <th className="py-2 px-3 text-secondary fw-semibold">Gasto</th>
                    <th className="py-2 px-3 text-secondary fw-semibold text-center">Amarre</th>
                    <th className="py-2 px-3 text-secondary fw-semibold text-center">Destino</th>
                  </tr>
                </thead>
                <tbody>
                  {reglas.length === 0 ? <tr><td colSpan={3} className="text-center text-muted py-5">Sin reglas configuradas.</td></tr> : reglas.map(r => (
                    <tr key={r.id} style={{ borderBottom: "1px solid #f1f3f4" }}>
                      <td className="px-3 py-2 fw-medium text-dark small">{r.tipo_gasto}</td>
                      <td className="px-3 py-2 text-center small">
                        <Badge style={{ background: "rgba(107,114,128,0.1)", color: "#374151", border: "1px solid rgba(107,114,128,0.25)", borderRadius: "6px" }} className="px-2 me-1">{r.cuenta_debe}</Badge>
                        →
                        <Badge style={{ background: "rgba(107,114,128,0.1)", color: "#374151", border: "1px solid rgba(107,114,128,0.25)", borderRadius: "6px" }} className="px-2 ms-1">{r.cuenta_haber}</Badge>
                      </td>
                      <td className="px-3 py-2 text-center fw-bold">{r.cuenta_destino || "—"}</td>
                    </tr>
                  ))}
                </tbody>
              </Table>
            </div>
          </Card>
        </Col>
      </Row>
    </div>
  );
};

export default PlanCuentasPCGE;
